/**
 * 施術録検索 (/treatment_records/init) feature constants.
 * Mirrors backend App\Helpers\TreatmentRecordConstants.
 */

/** 受診者番号 max length (patients.serial_number). */
export const SERIAL_NUMBER_MAX_LENGTH = 6;

/** 受診者名 max length (patient_informations.name). */
export const PATIENT_NAME_MAX_LENGTH = 255;

/** Page size for paginated list. */
export const LIST_PAGE_SIZE = 20;

/** Radio values for 絞り込み (search_radio). */
export const SEARCH_RADIO_SERIAL = '1' as const;
export const SEARCH_RADIO_NAME = '2' as const;

/** Radio values for 並び順 (sort_radio). */
export const SORT_BY_SERIAL = '1' as const;
export const SORT_BY_KANA = '2' as const;

/** Validation / toast messages specific to 施術録検索. */
export const YEARMONTH_REQUIRED_MESSAGE =
  '【提出年月】または【施術年月】を入力してください。';
export const NO_ROWS_MESSAGE = '対象のレセプトを選択してください。';
export const NO_SELECTION_MESSAGE = '受診者を選択してください。';
export const NO_PRINT_DATA_MESSAGE = '該当するデータはありませんでした。';
export const PDF_ERROR_MESSAGE = 'PDFの生成に失敗しました。';

/** 印刷面 radio values: 1=表面, 2=裏面, 3=両面. */
export const OUTPUT_RADIO_FRONT = 1 as const;
export const OUTPUT_RADIO_BACK = 2 as const;
export const OUTPUT_RADIO_BOTH = 3 as const;

export type OutputRadio =
  | typeof OUTPUT_RADIO_FRONT
  | typeof OUTPUT_RADIO_BACK
  | typeof OUTPUT_RADIO_BOTH;

/**
 * Front-side print flags (①~⑥ checkbox groups).
 * Mirrors backend PrintConsentTreatmentRecordRequest::flagFields() front subset.
 */
export const FRONT_FLAG_FIELDS = [
  // ① 治療院ID-受診者No
  'is_front_clinic_id',
  // ② 保険種別
  'is_health_insurance_cards_type',
  // ③ 公費負担医療
  'is_medical_aid_number',
  'is_medical_aid_patient_number',
  // ④ 保険証面 — 施術を受ける者
  'is_health_insurance_card_mark',
  'is_health_insurance_card_number',
  'is_health_insurance_card_expiration_date',
  'is_health_insurance_card_qualification_date',
  'is_patient_information_name',
  'is_patient_information_name_kana',
  'is_patient_sex',
  'is_patient_information_relationship_id',
  'is_patient_information_date_of_birth',
  'is_office_name',
  'is_health_insurance_cards_insurer_number',
  'is_health_insurance_cards_insurer_name',
  // ④ 保険証面 — 被保険者
  'is_health_insurance_cards_name',
  'is_health_insurance_cards_name_kana',
  'is_health_insurance_card_sex',
  'is_health_insurance_cards_date_of_birth',
  'is_health_insurance_cards_address',
  'is_health_insurance_cards_address_kana',
  // ⑤ 病名～転記
  'is_consents_disease_name',
  'is_consents_incident_date',
  'is_consents_first_treatment_date',
  'is_first_visit_date',
  'is_last_visit_date',
  'is_consents_days',
  'is_consents_treatment_count',
  'is_rezepts_outcome_type_id',
  // ⑥ 同意記録
  'is_consents_hospital_name',
  'is_consents_hospital_address',
  'is_consents_hospital_telephone',
  'is_consents_doctor_name',
  'is_consents_doctor_name_kana',
  'is_consents_consent_date',
  'is_consents_incident_cause',
  'is_consents_incident_date_type_id',
  'is_consents_symptom',
  'is_length',
  'is_pre_existing_illness',
  'is_consents_massage',
  'is_consents_orthomorphia',
  'is_consents_hot_treatment',
] as const;

/**
 * Back-side print flags (15-17 items — 2026/04/06 spec update).
 */
export const BACK_FLAG_FIELDS = [
  'is_back_clinic_id',
  'is_day_check',
  'is_visit_clinic_price',
  'is_massage_price',
  'is_orthomorphia_price',
  'is_hot_fomentation_price',
  'is_visit_treatment_price',
  'is_transportation_price',
  'is_special_area_price',
  'is_subtotal_price',
  'is_part_of_charge_day',
  'is_visits_massager_remarks',
  'is_remarks',
  'is_treatment_yearmonth',
  'is_billing_yearmonth',
  'is_total_price',
  'is_part_of_charge',
  'is_amount_claimed',
  'is_visits_count',
] as const;

export type FrontFlagField = (typeof FRONT_FLAG_FIELDS)[number];
export type BackFlagField = (typeof BACK_FLAG_FIELDS)[number];
export type PrintFlagField = FrontFlagField | BackFlagField;

/** All flag fields (front + back) — useful for "toggle all" operations. */
export const ALL_FLAG_FIELDS: readonly PrintFlagField[] = [
  ...FRONT_FLAG_FIELDS,
  ...BACK_FLAG_FIELDS,
];

// ─────────────────────────────────────────────────────────────────────────────
// 裏面 — per-service visible checkbox sets (BD section 27 / sheet 4.3.1 + 4.3.2)
// A2 + R2 both render 17 checkboxes but with different field sets and labels:
//   • A2 item 3 「通所」 is a SINGLE UI checkbox that toggles three DB fields
//     in lockstep (`is_visit_clinic_price` + `is_massage_price` +
//     `is_orthomorphia_price`). See `A2_VISIT_CLINIC_SYNC_FIELDS` below.
//   • R2 item 3/4/5 are THREE separate checkboxes labelled「通所部位」/
//    「変形徒手矯正術部位」/「温罨法又は温罨法・電療」mapped 1:1 to
//     `is_massage_price` / `is_orthomorphia_price` / `is_hot_fomentation_price`.
//   • A2 item 9 「一部負担金 (day)」(`is_part_of_charge_day`) is A2-only —
//     R2 has no day-level copayment column on the back side.
//   • R2 has no `is_visit_clinic_price` checkbox; that flag is A2-only.
// ─────────────────────────────────────────────────────────────────────────────

/** A2 item 3 「通所」master toggles all three flags in sync. */
export const A2_VISIT_CLINIC_SYNC_FIELDS: readonly BackFlagField[] = [
  'is_visit_clinic_price',
  'is_massage_price',
  'is_orthomorphia_price',
];

/** A2 visible 裏面 checkboxes (17). `is_visit_clinic_price` represents the
 *  「通所」master — the modal renders only this one and keeps
 *  `is_massage_price` + `is_orthomorphia_price` synced under the hood. */
export const BACK_FLAG_FIELDS_A2: readonly BackFlagField[] = [
  'is_back_clinic_id',
  'is_day_check',
  'is_visit_clinic_price',
  'is_hot_fomentation_price',
  'is_visit_treatment_price',
  'is_special_area_price',
  'is_transportation_price',
  'is_subtotal_price',
  'is_part_of_charge_day',
  'is_visits_massager_remarks',
  'is_remarks',
  'is_treatment_yearmonth',
  'is_billing_yearmonth',
  'is_total_price',
  'is_part_of_charge',
  'is_amount_claimed',
  'is_visits_count',
];

/** R2 visible 裏面 checkboxes (17). */
export const BACK_FLAG_FIELDS_R2: readonly BackFlagField[] = [
  'is_back_clinic_id',
  'is_day_check',
  'is_massage_price',
  'is_orthomorphia_price',
  'is_hot_fomentation_price',
  'is_visit_treatment_price',
  'is_special_area_price',
  'is_transportation_price',
  'is_subtotal_price',
  'is_visits_massager_remarks',
  'is_remarks',
  'is_treatment_yearmonth',
  'is_billing_yearmonth',
  'is_total_price',
  'is_part_of_charge',
  'is_amount_claimed',
  'is_visits_count',
];

// ────────────────────────────────────────────────────────────────────────────
// Front-side group definitions (①~⑥)
// Mirrors A2 legacy `culum_control()` + `change_btn()` mapping so preview
// overlays (RANGE_FLAG_MAP below) and the master toggles stay in sync.
// ────────────────────────────────────────────────────────────────────────────

/** Fields covered by ③ 公費負担医療. */
export const FRONT_GROUP_3_FIELDS = [
  'is_medical_aid_number',
  'is_medical_aid_patient_number',
] as const satisfies readonly FrontFlagField[];

/** Fields covered by ④ 保険証面 (施術を受ける者 + 被保険者). */
export const FRONT_GROUP_4_FIELDS = [
  'is_health_insurance_card_mark',
  'is_health_insurance_card_number',
  'is_health_insurance_card_expiration_date',
  'is_health_insurance_card_qualification_date',
  'is_patient_information_name',
  'is_patient_information_name_kana',
  'is_patient_sex',
  'is_patient_information_relationship_id',
  'is_patient_information_date_of_birth',
  'is_office_name',
  'is_health_insurance_cards_insurer_number',
  'is_health_insurance_cards_insurer_name',
  'is_health_insurance_cards_name',
  'is_health_insurance_cards_name_kana',
  'is_health_insurance_card_sex',
  'is_health_insurance_cards_date_of_birth',
  'is_health_insurance_cards_address',
  'is_health_insurance_cards_address_kana',
] as const satisfies readonly FrontFlagField[];

/** Fields covered by ⑤ 病名～転記. */
export const FRONT_GROUP_5_FIELDS = [
  'is_consents_disease_name',
  'is_consents_incident_date',
  'is_consents_first_treatment_date',
  'is_first_visit_date',
  'is_last_visit_date',
  'is_consents_days',
  'is_consents_treatment_count',
  'is_rezepts_outcome_type_id',
] as const satisfies readonly FrontFlagField[];

/** Fields covered by ⑥ 同意記録 (includes R2-specific flags; harmless for A2). */
export const FRONT_GROUP_6_FIELDS = [
  'is_consents_hospital_name',
  'is_consents_hospital_address',
  'is_consents_hospital_telephone',
  'is_consents_doctor_name',
  'is_consents_doctor_name_kana',
  'is_consents_consent_date',
  'is_consents_incident_cause',
  'is_consents_incident_date_type_id',
  'is_consents_symptom',
  'is_pre_existing_illness',
  'is_length',
  'is_consents_massage',
  'is_consents_orthomorphia',
  'is_consents_hot_treatment',
] as const satisfies readonly FrontFlagField[];

/**
 * Fields covered by ⑦ 施術の種類 (R2 only).
 * R2 legacy `list_template.blade.php` splits out 施術の種類 + 往療距離 into its
 * own master toggle group. Clinic-wide does not render the length textbox
 * (per legacy `@if(!empty($param['patient_id']))` gate).
 */
export const FRONT_GROUP_7_FIELDS = [
  'is_consents_massage',
  'is_consents_orthomorphia',
  'is_consents_hot_treatment',
  'is_length',
] as const satisfies readonly FrontFlagField[];

/**
 * R2 variant of ⑥: excludes the four fields that R2 moves to ⑦ (施術の種類).
 * Without this, toggling ⑥ master on R2 would also flip the ⑦ subfields.
 */
export const FRONT_GROUP_6_R2_FIELDS = FRONT_GROUP_6_FIELDS.filter(
  f => !(FRONT_GROUP_7_FIELDS as readonly string[]).includes(f)
) as readonly FrontFlagField[];
